import React, { useState } from 'react';
import { TWEET_TEMPLATES } from '../services/templateService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface TemplateSelectorProps {
  selectedTemplates: string[];
  onSelect: (templates: string[]) => void;
  maxSelection?: number;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplates,
  onSelect,
  maxSelection = 4,
}) => {
  const { t } = useT();
  const [isOpen, setIsOpen] = useState(false);

  const toggleTemplate = (type: string) => {
    if (selectedTemplates.includes(type)) {
      onSelect(selectedTemplates.filter(t => t !== type));
    } else {
      if (selectedTemplates.length < maxSelection) {
        onSelect([...selectedTemplates, type]);
      }
    }
  };

  const handleRandom = () => {
    const shuffled = [...TWEET_TEMPLATES].sort(() => Math.random() - 0.5);
    const random = shuffled.slice(0, maxSelection).map(t => t.type);
    onSelect(random);
  };

  const handleClear = () => {
    onSelect([]);
  };

  const selectedCount = selectedTemplates.length;
  const hasSelection = selectedCount > 0;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all',
          hasSelection
            ? 'bg-brand/10 border-brand text-brand'
            : 'bg-surface-2 border-border text-text-muted hover:border-brand'
        )}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3h18v18H3z"/><path d="M3 3l8 8"/><path d="M3 7h4"/><path d="M3 11h4"/><path d="M3 15h4"/><path d="M7 3v4"/><path d="M11 3v4"/><path d="M15 3v4"/></svg>
          <div className="text-left">
            <p className="text-sm font-medium">
              {hasSelection
                ? t('templateSelector.selectingCount', { count: selectedCount, max: maxSelection })
                : t('templateSelector.selectOptional')}
            </p>
            {hasSelection && (
              <p className="text-xs opacity-75">
                {selectedTemplates.join(', ')}
              </p>
            )}
          </div>
        </div>
        <span className={cn(
          'text-sm transition-transform',
          isOpen && 'rotate-180'
        )}>
          ▼
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-20 mt-2 w-full max-h-[500px] overflow-y-auto bg-surface-2 border border-border rounded-lg shadow-xl">
          {/* Header */}
          <div className="sticky top-0 bg-surface-2 border-b border-border p-3 flex items-center justify-between">
            <p className="text-sm text-text-muted">
              {t('templateSelector.selectTitle', { count: selectedCount, max: maxSelection })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleRandom}
                className="px-3 py-1 text-xs bg-brand/10 text-brand rounded hover:bg-brand/20"
              >
                {t('templateSelector.random')}
              </button>
              {hasSelection && (
                <button
                  onClick={handleClear}
                  className="px-3 py-1 text-xs bg-danger/10 text-danger rounded hover:bg-danger/20"
                >
                  {t('common.clear')}
                </button>
              )}
            </div>
          </div>

          {/* Template List */}
          <div className="p-2 space-y-1">
            {TWEET_TEMPLATES.map((template) => {
              const isSelected = selectedTemplates.includes(template.type);
              const isDisabled = !isSelected && selectedCount >= maxSelection;

              return (
                <button
                  key={template.type}
                  onClick={() => !isDisabled && toggleTemplate(template.type)}
                  disabled={isDisabled}
                  className={cn(
                    'w-full text-left p-3 rounded-lg transition-all',
                    isSelected && 'bg-brand/10 border border-brand',
                    !isSelected && !isDisabled && 'hover:bg-brand/5 border border-transparent',
                    isDisabled && 'opacity-40 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {isSelected && <svg className="w-4 h-4 text-brand" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>}
                      <p className={cn(
                        'font-medium text-sm',
                        isSelected ? 'text-brand' : 'text-text-primary'
                      )}>
                        {template.type}
                      </p>
                    </div>
                    {!isSelected && isDisabled && (
                      <span className="text-xs text-text-muted">{t('templateSelector.limit')}</span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mb-2">
                    {template.description}
                  </p>
                  <div className="text-xs">
                    <p className="text-text-muted mb-1">{t('templateSelector.pattern')}</p>
                    <code className="text-accent bg-surface-1 px-2 py-1 rounded">
                      {template.pattern}
                    </code>
                  </div>
                  <div className="text-xs mt-2">
                    <p className="text-text-muted mb-1">{t('templateSelector.example')}</p>
                    <p className="text-text-primary italic">
                      {template.example}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-surface-2 border-t border-border p-3">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full py-2 bg-brand text-white rounded-lg hover:bg-brand/90"
            >
              {t('common.done')}
            </button>
          </div>
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
